import React, {useState, useEffect} from 'react';
import _ from 'lodash';
import { Row, Col, Button, Alert } from 'reactstrap';

import { Card } from '../base/components/CardAccordion';
import PropControl from '../base/components/PropControl';
import DataStore, { getDataPath } from '../base/plumbing/DataStore';
import C from '../C';
import FilterButton from '../base/data/FilterButton';
import SearchQuery from '../base/searchquery';

/**
 * Controls for a single category
 * @param {Object} filterBtns the filterBtns object 
 * @param {String} category
 * @param {String[]} dpath
 */
function FilterButtonCategoryControls({filterBtns, category, dpath}) {
	if (!filterBtns[category] || !filterBtns[category].length) return null;

	const [collapsed, setCollapsed] = useState(true);

	const addNewBtn = () => {
		if (!filterBtns) filterBtns = {};
		if (!filterBtns[category]) filterBtns[category] = [];
		filterBtns[category].push(new FilterButton());
		DataStore.setValue(dpath, filterBtns);
	};

	const deleteCategory = () => {
		if (confirm("Are you sure you want to delete this category?\nThis will remove ALL FILTERS belonging to it!")) {
			// HACK : setting entry to null just removes it, which isn't saved. Enter a new array to make sure data is saved
			filterBtns[category] = [];
			DataStore.setValue(dpath, filterBtns);
		}
	};

	//const nonNullBtns = filterBtns && filterBtns[category].filter(x => x);

	return <div style={{background:"#f2f2f2"}} className="px-2">
		<hr/>
		<div className="d-flex flex-row justify-content-between">
			<h4>Category: {category} ({filterBtns[category].length})</h4>
			<Button color="secondary" onClick={() => setCollapsed(!collapsed)}>{collapsed ? "+" : "-"}</Button>
		</div>
		{!collapsed && <>
			<Row>
				{filterBtns[category] && <div className="d-flex flex-row w-100 overflow-auto">
					{filterBtns[category].map((filterBtn, i) => <FilterButtonControl key={i} btn={filterBtn} id={i} category={category} dpath={dpath} filterBtns={filterBtns}/>)}
				</div>}
			</Row>
			<Button color="primary" onClick={addNewBtn}>Add new filter</Button>&nbsp;
			<Button color="danger" onClick={deleteCategory}>Delete category</Button>
		</>}
		<hr/>
	</div>;
}


/**
 * Controls for a single filter button
 */
function FilterButtonControl({dpath, btn, id, category, filterBtns}) {
	//if (!btn) return null; // || btn.dataState === "removed") return null;

	const categoryDPath = dpath.concat(category);
	const btnDPath = categoryDPath.concat(id);

	const [collapsed, setCollapsed] = useState(true);
	
	const widgetPath = ["widget", "ImpactHubFilterButtonControls"].concat(category).concat("i"+id);
	const tagsPath = widgetPath.concat("filterTags");

	const displayName = btn && btn.displayName;
	const imgUrl = btn && btn.imgUrl;
	const query = btn && btn.query;

	let isDupeQuery = false;
	if (query && filterBtns[category]) {
		filterBtns[category].forEach(otherBtn => {
			if (otherBtn !== btn && otherBtn && otherBtn.query === query) {
				isDupeQuery = true;
			}
		});
	}

	// Check query is valid as normal tags and if so parse
	let validTags = true;
	let tags = [];
	if (query) {
		// Try and decipher a list of tags from the query
		const sq = new SearchQuery(query);
		const tree = sq.tree;
		// If there is 1 tag, tree will be ["AND", {tags:x}]
		if (tree[0] === "AND") {
			if (tree.length !== 2 || !tree[1].tags) validTags = false;
			else tags.push(tree[1].tags);
		} else if (tree[0] === "OR") {
			// If there are multiple tags, tree will be ["OR", {tags:x}, {tags:y}, ...]
			const vals = tree.splice(1, tree.length);
			vals.forEach(val => {
				if (!val.tags) {
					validTags = false;
					return;
				}
				tags.push(val.tags);
			});
		} else {
			console.log("Invalid tags on ", tree[0], "???", tree.length, tree[1].tags);
			// There is no scenario where auto-generated queries should not start with AND or OR
			validTags = false;
		}
	}

	const tagsStr = DataStore.getValue(tagsPath);
	const tagsInPropControl = tagsStr ? tagsStr.split(" ") : [];
	const areTagsModified = !_.isEqual(tags.sort(), tagsInPropControl.sort());

	const deleteFilterBtn = () => {
		if (confirm("Are you sure you want to delete this filter?")) {
			let categoryObj = DataStore.getValue(categoryDPath);
			if (categoryObj.length !== 1 || confirm("This is the last filter in this category, deleting it will remove the category. Continue?")) {
				categoryObj.splice(id, 1);//if (categoryObj[id]) FilterButton.remove(categoryObj[id]);
				DataStore.setValue(categoryDPath, categoryObj);
			}
		}
	};

	const createQueryForTags = () => {
		if (validTags || confirm("Are you sure you want to overwrite the custom query?")) {
			if (!tagsStr) DataStore.setValue(btnDPath.concat("query"), "");
			const newTags = tagsStr.split(" ");
			let sq = SearchQuery.setPropOr(null, "tags", newTags);
			DataStore.setValue(btnDPath.concat("query"), sq.query);
		}
	};

	useEffect(() => {
		DataStore.setValue(tagsPath, tags.join(" "));
	}, []);

	const allowQueryEdit = DataStore.getValue(widgetPath.concat("showQuery"));

	return <Col xs={4} className="py-1">
		<div style={{border:"1px solid grey", borderRadius:5, background:"white"}} className="p-2">
			<div className="w-50 d-inline-block">
				<h3>{displayName || "(Empty)"}</h3>
				<Button color="secondary" onClick={() => setCollapsed(!collapsed)}><b>{collapsed ? "+" : "-"}</b></Button>&nbsp;
				<Button color="danger" onClick={deleteFilterBtn}>Delete filter</Button>
			</div>
			<div className="w-50 d-inline-block">
				{imgUrl && <img src={imgUrl} className="w-100"/>}
			</div>
			{!query && <><br/><br/><Alert color="warning">Missing query!<br/><small>Filters will not work without a query</small></Alert></>}
			{isDupeQuery && <><br/><br/><Alert color="danger">Duplicate query<br/><small>This filter has the same query as another. Duplicate queries are not allowed.</small></Alert></>}
			{query && <><br/><br/></>}
			{!collapsed &&
				<>
					<PropControl label="Display name" type="text" prop="displayName" path={btnDPath}/>
					<PropControl label="Display image" type="imgUpload" prop="imgUrl" path={btnDPath} help="If set, will show instead of display name" version="standard" />
					<PropControl label="Tags" type="text" prop="filterTags" path={["widget", "ImpactHubFilterButtonControls", category, "i"+id]}
						help={validTags ? "Which tags to search for with this filter, space separated list" : <span style={{color:"red"}}>Could not decipher tags from custom query.</span>}/>
					{areTagsModified && <>
						<small style={{color:"orange"}}>Don't forget to save your new tags by clicking the button below!</small>
						<br/>
					</>}
					<Button color={areTagsModified ? "primary" : "secondary"} onClick={createQueryForTags}>Create query from tags</Button>
					<br/><br/>
					<PropControl label="Edit query directly" prop="showQuery" path={widgetPath} help="For developers only" type="checkbox"/>
					{allowQueryEdit && <PropControl label="Custom Query" type="text" prop="query" path={btnDPath}/>}
				</>}
		</div>
	</Col>;
}


function ImpactHubFilterButtonControls({campaign, campaignPath}) {
	const [collapsed, setCollapsed] = useState(true);
	const [formError, setFormError] = useState(false);

	let cpath = campaignPath || getDataPath({status:C.KStatus.DRAFT,type:C.TYPES.Campaign,id:campaign.id});
    
	const filterBtnsPath = cpath.concat("filterButtons");
	let filterBtns = DataStore.getValue(filterBtnsPath);

	const addNewCategory = () => {
		const newName = DataStore.getValue(["widget", "ImpactHubFilterButtonControls", "newCategoryName"]);
		if (!newName) {
			setFormError("You must specify a name to create a new category!");
			return;
		}
		if (!filterBtns) filterBtns = {};
		if (filterBtns[newName] && filterBtns[newName].length) {
			setFormError("That category already exists!");
			return;
		}
		setFormError("");
		const newButton = new FilterButton();
		filterBtns[newName] = [newButton];
		DataStore.setValue(filterBtnsPath, filterBtns);
	};

	const applyRawData = () => {
		const newData = DataStore.getValue(["widget", "ImpactHubFilterButtonControls", "rawData"]);
		let newDataObj;
		try {
			newDataObj = JSON.parse(newData);
		} catch (e) {
			setFormError("Could not parse JSON data!");
			return;
		}
		setFormError(null);
		if (confirm("!!!WARNING!!! This will override EVERYTHING in the filters. Are you sure you want to do this? (If you are not a developer, click CANCEL)")) {
			DataStore.setValue(filterBtnsPath, newDataObj);
		}
	};

	useEffect(() => {
		if (filterBtns) DataStore.setValue(["widget", "ImpactHubFilterButtonControls", "rawData"], JSON.stringify(filterBtns, null, 2));
	}, [filterBtns]);

	const unlockDataEdit = DataStore.getValue(["widget", "ImpactHubFilterButtonControls", "unlockRawData"]);

	return <Card title="Custom filters" collapse={collapsed} onHeaderClick={() => setCollapsed(!collapsed)}>
		<p>
			Customize different filters to display on the Impact Hub<br/>
			<small>
				The Impact Hub can show a variety of buttons which allow the viewer to filter ads by categories, e.g. by advertiser.<br/>
				More filters can be added here, using custom queries. Ask a developer to manage these.
			</small>
		</p>
		<PropControl type="checkbox" path={campaignPath} prop="showVertiserFilters" label="Show 'filter by advertiser'" help="The Impact Hub can generate filters by advertiser, if needed."/>
		{filterBtns && Object.keys(filterBtns).sort().map(category => <FilterButtonCategoryControls category={category} filterBtns={filterBtns} dpath={filterBtnsPath} key={category}/>)}
		<br/>
		<PropControl type="text" label="New category name" prop="newCategoryName" path={["widget", "ImpactHubFilterButtonControls"]} className="w-25"/>
		<Button color="primary" onClick={addNewCategory}>Add new category</Button>
		{formError && <>
			<br/><br/>
			<Alert color="danger">{formError}</Alert>
		</>}
		<br/><br/>
		<PropControl type="checkbox" label="Unlock data copy/paste" prop="unlockRawData" path={["widget", "ImpactHubFilterButtonControls"]} help="DEVELOPER ONLY. Copy/paste filter data from/to here to easily use the same filters from another Impact Hub page."/>
		{unlockDataEdit && <>
			<PropControl type="textarea" prop="rawData" path={["widget", "ImpactHubFilterButtonControls"]} label="Raw data"/>
			<Button color="danger" onClick={applyRawData}>Apply new data</Button>
		</>}
	</Card>;
}


export default ImpactHubFilterButtonControls;
